import { computed, ref, type Ref } from 'vue';
import { useRoute, type LocationQueryRaw, type RouteLocationRaw } from 'vue-router';
import { DocumentRevisionStatus } from '@/shared/lib';
import {
  MATRIX_RECORD_FIELDS_NOT_EDITABLE_MESSAGE,
  isMatrixRecordFieldsNotEditableError,
  type Matrix,
} from '../api/form-builder.api';
import type { HeaderRecordFields } from '../lib/form-header-fields';

export const DRAFTS_ORIGIN = 'rascunhos';
export const REJECTED_ORIGIN = 'reprovados';

export const DRAFTS_LIST_ROUTE: RouteLocationRaw = {
  name: 'GestaoRegistrosModelos',
  query: { tab: 'rascunhos' },
};

export const REJECTED_LIST_ROUTE: RouteLocationRaw = {
  name: 'GestaoRegistrosModelos',
  query: { tab: 'reprovados' },
};

const MODELS_ROUTE: RouteLocationRaw = { name: 'GestaoRegistrosModelos' };
const RECORDS_FALLBACK_ROUTE: RouteLocationRaw = '/gestao-registros/modelos';

export const DRAFT_UPDATED_MESSAGE = 'Rascunho atualizado com sucesso';
export const DRAFT_UPDATE_ERROR_MESSAGE = 'Erro ao atualizar rascunho';
const DRAFT_LOAD_ERROR_MESSAGE = 'Erro ao carregar rascunho';
const RECORD_LOAD_ERROR_MESSAGE = 'Erro ao carregar registro';

const HOMOLOGATED_STATUSES: ReadonlyArray<string> = [
  DocumentRevisionStatus.CURRENT,
  DocumentRevisionStatus.OBSOLETE,
  DocumentRevisionStatus.INACTIVE,
];

const REJECTED_STATUSES = ['FAILED_APPROVAL', 'FAILED_VERIFICATION'];

export function isRejectedMatrix(matrix: Pick<Matrix, 'isDraft' | 'status'>): boolean {
  return !matrix.isDraft && REJECTED_STATUSES.includes(matrix.status ?? '');
}

interface PrefixBaseline {
  matrixId: number;
  prefix: string;
}

interface UseRecordEditModeOptions {
  isEditMode: Ref<boolean>;
  loadedMatrix: Ref<Matrix | null>;
  getHeaderRecordFields: () => HeaderRecordFields;
}

export function useRecordEditMode({
  isEditMode,
  loadedMatrix,
  getHeaderRecordFields,
}: UseRecordEditModeOptions) {
  const route = useRoute();

  const cameFromDrafts = computed(() => isEditMode.value && route.query.origem === DRAFTS_ORIGIN);
  const cameFromRejected = computed(
    () => isEditMode.value && route.query.origem === REJECTED_ORIGIN
  );
  const isDraftEditMode = computed(() => isEditMode.value && loadedMatrix.value?.isDraft === true);
  const isRejectedEditMode = computed(
    () => isEditMode.value && loadedMatrix.value !== null && isRejectedMatrix(loadedMatrix.value)
  );
  const lockHeaderRqField = computed(() => isRejectedEditMode.value);

  const exitRoute = computed<RouteLocationRaw>(() => {
    if (cameFromDrafts.value || isDraftEditMode.value) return DRAFTS_LIST_ROUTE;
    if (cameFromRejected.value || isRejectedEditMode.value) return REJECTED_LIST_ROUTE;
    return MODELS_ROUTE;
  });

  const loadFailure = computed(() => {
    if (cameFromDrafts.value) {
      return { message: DRAFT_LOAD_ERROR_MESSAGE, target: DRAFTS_LIST_ROUTE };
    }
    if (cameFromRejected.value) {
      return { message: RECORD_LOAD_ERROR_MESSAGE, target: REJECTED_LIST_ROUTE };
    }
    return { message: RECORD_LOAD_ERROR_MESSAGE, target: RECORDS_FALLBACK_ROUTE };
  });

  function rejectsLoadedMatrix(matrix: Matrix): boolean {
    if (cameFromDrafts.value) return !matrix.isDraft;
    if (cameFromRejected.value) return !isRejectedMatrix(matrix);
    return false;
  }

  const savedDraftQuery = computed<LocationQueryRaw>(() =>
    cameFromRejected.value ? { origem: DRAFTS_ORIGIN } : route.query
  );

  const lockedApprovalRevision = computed<string | null>(() => {
    const revision = loadedMatrix.value?.revision;
    if (!isRejectedEditMode.value || revision == null) return null;
    return String(revision).padStart(2, '0');
  });

  const prefixBaseline = ref<PrefixBaseline | null>(null);

  function captureRecordFieldsBaseline() {
    const matrix = loadedMatrix.value;
    prefixBaseline.value = matrix
      ? { matrixId: matrix.id, prefix: getHeaderRecordFields().prefix }
      : null;
  }

  function resolvePrefixBaseline(matrix: Matrix): string {
    const baseline = prefixBaseline.value;
    return baseline?.matrixId === matrix.id ? baseline.prefix : matrix.prefix.trim();
  }

  // Mirrors the server: a legacy header may print an RQ other than the saved column, or none, so
  // keeping either the column or what the header printed at load is no change.
  function hasChangedPrefix(matrix: Matrix): boolean {
    const current = getHeaderRecordFields().prefix;
    return current !== matrix.prefix.trim() && current !== resolvePrefixBaseline(matrix);
  }

  function hasChangedRecordFields(): boolean {
    const matrix = loadedMatrix.value;
    return isDraftEditMode.value && matrix !== null && hasChangedPrefix(matrix);
  }

  function hasChangedHomologatedPrefix(): boolean {
    const matrix = loadedMatrix.value;
    return (
      isEditMode.value &&
      matrix !== null &&
      HOMOLOGATED_STATUSES.includes(matrix.status ?? '') &&
      hasChangedPrefix(matrix)
    );
  }

  function resolveDraftUpdateErrorMessage(error: unknown): string {
    return isMatrixRecordFieldsNotEditableError(error)
      ? MATRIX_RECORD_FIELDS_NOT_EDITABLE_MESSAGE
      : DRAFT_UPDATE_ERROR_MESSAGE;
  }

  return {
    cameFromDrafts,
    cameFromRejected,
    isDraftEditMode,
    isRejectedEditMode,
    lockHeaderRqField,
    exitRoute,
    loadFailure,
    rejectsLoadedMatrix,
    savedDraftQuery,
    lockedApprovalRevision,
    captureRecordFieldsBaseline,
    hasChangedRecordFields,
    hasChangedHomologatedPrefix,
    resolveDraftUpdateErrorMessage,
  };
}
